import { useState, useEffect, useRef } from 'react';
import { AuthState } from '../types/api';
import { auth } from '../services/api';
import { performGlobalSignoutCleanup } from '../services/api';

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
  });

  // Используем ref для отслеживания инициализации
  const isInitialized = useRef(false);

  useEffect(() => {
    // Проверяем авторизацию через API при загрузке только один раз
    if (!isInitialized.current) {
      hydrateFromServer();
      isInitialized.current = true;
    }
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
    };
    window.addEventListener('infiano:session-expired', onSessionExpired);
    return () => window.removeEventListener('infiano:session-expired', onSessionExpired);
  }, []);

  const hydrateFromServer = async () => {
    try {
      const response = await auth.checkStatus();

      if (response.success && response.data) {
        if (response.data.authenticated) {
          // Подтягиваем профиль, чтобы иметь роль (admin/user) и баланс токенов
          try {
            const profileResp = await auth.getProfile();
            const role = profileResp?.success ? profileResp.data?.role : undefined;
            const token_balance = profileResp?.success ? profileResp.data?.token_balance : undefined;
            const id = (profileResp?.success ? profileResp.data?.id : undefined) || response.data.user_id;

            setAuthState({
              isAuthenticated: true,
              user: {
                id,
                email: response.data.email,
                name: response.data.name,
                company_name: response.data.company_name,
                role,
                token_balance,
              },
              isLoading: false,
            });
          } catch (e) {
            // Если профиль недоступен — всё равно считаем пользователя залогиненным
            setAuthState({
              isAuthenticated: true,
              user: {
                id: response.data.user_id,
                email: response.data.email,
                name: response.data.name,
                company_name: response.data.company_name,
              },
              isLoading: false,
            });
          }
        } else {
          setAuthState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
          });
          // Если сервер сообщает, что пользователь не авторизован — чистим состояние
          performGlobalSignoutCleanup();
        }
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          isLoading: false,
        });
        performGlobalSignoutCleanup();
      }
    } catch (error) {
      console.error('Auth status check error:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
      performGlobalSignoutCleanup();
    }
  };

  const login = (email: string, name?: string, company_name?: string) => {
    // При использовании куков токены устанавливаются сервером
    // Нам нужно только обновить локальное состояние
    setAuthState({
      isAuthenticated: true,
      user: {
        email,
        name,
        company_name,
      },
      isLoading: true,
    });
    // Сразу подтягиваем роль/профиль с сервера, чтобы показать admin-only UI без перезагрузки
    hydrateFromServer();
  };

  const logout = async () => {
    try {
      await auth.signout();
    } catch (error: any) {
      // Если ошибка — это 401, не логируем её как ошибку
      if (!(error && error.response && error.response.status === 401)) {
        console.error('Signout API error:', error);
      }
      // Если 401 — просто игнорируем, всё равно делаем logout
    } finally {
      // Глобальная очистка состояния приложения
      performGlobalSignoutCleanup();
      // Очищаем локальное состояние
      setAuthState({
        isAuthenticated: false,
        user: null,
        isLoading: false,
      });
    }
  };

  return {
    ...authState,
    login,
    logout,
  };
};
